import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../api/axios';

const AuthContext = createContext(null);

const REFRESH_MIN_GAP_MS = 60000;

// Kept outside the component (and taking the ref explicitly) so it never
// closes over component state — that keeps it out of any hook's dependency
// array entirely, instead of relying on an eslint-disable comment to quiet
// exhaustive-deps (which broke the EC2 build when that plugin didn't resolve).
const dedupeCall = (inFlightRef, key, fn) => {
  if (inFlightRef.current[key]) return inFlightRef.current[key];
  const p = fn().finally(() => { delete inFlightRef.current[key]; });
  inFlightRef.current[key] = p;
  return p;
};

export const AuthProvider = ({ children }) => {
  // SEC-06: auth is now an httpOnly session cookie — nothing about it is
  // readable from JS, so on first load we don't know who (if anyone) is
  // signed in until the server tells us via GET /auth/me. `loading` covers
  // that gap so routing doesn't bounce a signed-in user to /login before
  // the check comes back.
  const [user, setUser] = useState(null);
  const [perms, setPerms] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * PERF-01: these were firing several times per navigation — the mount effect
   * called loadPerms() and refreshUser(), and refreshUser() called loadPerms()
   * again, with an unthrottled repeat on every window focus.
   * In-flight calls are now shared and focus refreshes are throttled.
   */
  const inFlight = useRef({});
  const lastRefresh = useRef(0);

  const loadPerms = useCallback(async () => dedupeCall(inFlight, 'perms', async () => {
    try {
      const { data } = await api.get('/permissions/my/perms');
      setPerms(data);
    } catch {
      /* ignore */
    }
  }), []);

  const refreshUser = useCallback(async () => dedupeCall(inFlight, 'me', async () => {
    try {
      const { data } = await api.get('/auth/me');
      if (!data?.id) {
        setUser(null);
        setAccounts([]);
        setActiveKey(null);
        return;
      }
      const { accounts: linked, active_key: key, ...me } = data;
      setUser(me);
      setAccounts(linked || []);
      setActiveKey(key || null);
      await loadPerms();
    } catch {
      setUser(null);
      setAccounts([]);
      setActiveKey(null);
    } finally {
      setLoading(false);
    }
  }), [loadPerms]);

  // Runs once per full page load — subsequent account changes (login,
  // switch, logout) trigger a hard navigation rather than relying on this
  // effect to re-fire, so per-page component state resets cleanly.
  useEffect(() => {
    lastRefresh.current = Date.now();
    refreshUser();

    const refresh = () => {
      const now = Date.now();
      if (now - lastRefresh.current < REFRESH_MIN_GAP_MS) return;
      lastRefresh.current = now;
      refreshUser();
    };
    window.addEventListener('focus', refresh);
    window.addEventListener('tychi-perms-changed', loadPerms);
    return () => {
      window.removeEventListener('focus', refresh);
      window.removeEventListener('tychi-perms-changed', loadPerms);
    };
  }, [refreshUser, loadPerms]);

  // Adopt a user object we just got back from a login-style POST (the
  // session cookie for it is already set by that response). Kicks off a
  // background refresh to fill in accounts/perms without blocking the
  // caller's redirect.
  const saveSession = (u) => {
    setUser(u);
    setLoading(false);
    refreshUser();
  };

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    saveSession(data.user);
    return data.user;
  };

  const adminLogin = async (email, password) => {
    const { data } = await api.post('/auth/admin-login', { email, password });
    saveSession(data.user);
    return data;
  };

  const register = async (name, email, password, department) => {
    const { data } = await api.post('/auth/register', { name, email, password, department });
    saveSession(data.user);
    return data.user;
  };

  // Instantly switch to another already-signed-in account (no password).
  const switchAccount = async (key) => {
    await api.post('/auth/switch-account', { key });
    window.location.assign('/dashboard');
  };

  // Remove a saved account; if it's the active one, fall back to another or sign out
  const removeAccount = async (key) => {
    const { data } = await api.delete(`/auth/sessions/${key}`);
    if (!data.reloaded) {
      // Not the active account — just drop it from the in-memory list.
      setAccounts((list) => list.filter((a) => a.key !== key));
      return;
    }
    window.location.assign(data.signedIn ? '/dashboard' : '/login');
  };

  // Sign out the current account; stay signed in to another if available
  const logout = async () => {
    const { data } = await api.post('/auth/logout');
    window.location.assign(data.signedIn ? '/dashboard' : '/login');
  };

  /**
   * What this account may see.
   *
   * Admins used to short-circuit to true here, which meant the browser decided
   * an admin could see everything regardless of what the server said. That was
   * harmless while the server agreed — it grants admins every permission — but
   * it stopped being harmless the moment a module was gated to a named list:
   * the links appeared for every admin and the API then refused them, which
   * reads as a broken page rather than a closed door.
   *
   * So once permissions have loaded, they are the answer for everybody. The
   * server still grants admins everything it is not deliberately withholding,
   * so no admin loses anything they had. The admin shortcut survives only for
   * the moment before permissions arrive, where it is a loading state and not
   * a decision.
   */
  const can = (module) => {
    if (!user) return false;
    if (!perms) {
      if (user.role === 'admin') return true;
      const loadingDefaults = { dashboard: 1, tickets: 1, my_tasks: 1 };
      return !!loadingDefaults[module];
    }
    return !!perms[module];
  };

  return (
    <AuthContext.Provider value={{
      user,
      perms,
      accounts,
      activeKey,
      loading,
      viewMode: 'admin',
      can,
      login,
      adminLogin,
      register,
      saveSession,
      switchAccount,
      removeAccount,
      logout,
      loadPerms,
      refreshUser,
    }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
